import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Home, ArrowLeft, CheckCircle2, Tag } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  SITE_BASE_URL,
  getCategoryBySlug,
  PRODUCT_CATEGORIES,
} from '../data/productCategories';
import { getItemsForBrand, itemPath } from '../data/productItems';
import {
  buildProductPlaceholder,
  buildProductImageAlt,
} from '../lib/productPlaceholder';

const Breadcrumb = ({ categoryTitle }) => (
  <nav aria-label="Breadcrumb" className="mb-8">
    <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-400">
      <li>
        <Link to="/" className="hover:text-[#ff6b35] transition-colors flex items-center gap-1">
          <Home size={14} aria-hidden="true" />
          Home
        </Link>
      </li>
      <li aria-hidden="true">
        <ChevronRight size={14} />
      </li>
      <li>
        <Link to="/#products" className="hover:text-[#ff6b35] transition-colors">
          Product categories
        </Link>
      </li>
      <li aria-hidden="true">
        <ChevronRight size={14} />
      </li>
      <li className="text-[#ff6b35] font-medium" aria-current="page">
        {categoryTitle}
      </li>
    </ol>
  </nav>
);

const CategoryLanding = () => {
  const { slug } = useParams();
  const category = getCategoryBySlug(slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex flex-col">
        <Navigation />
        <main className="flex-grow pt-28 pb-20 px-4 text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Category not found</h1>
          <p className="text-gray-400 mb-8 max-w-md mx-auto">
            This spare parts category does not exist or may have moved.
          </p>
          <Button asChild className="bg-[#ff6b35] hover:bg-[#ff8c42] text-white">
            <Link to="/">Return home</Link>
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const currentUrl = `${SITE_BASE_URL}/category/${category.slug}`;
  const ogImage = `${SITE_BASE_URL}/trade-zone-logo.png`;
  const items = category.kind === 'brand' ? getItemsForBrand(category.slug) : [];
  const hasItems = items.length > 0;
  const brandLabel = category.brandLabel || category.title;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_BASE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Product categories',
        item: `${SITE_BASE_URL}/#products`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: category.title,
        item: currentUrl,
      },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.seoTitle,
    description: category.seoDescription,
    url: currentUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Trade Zone Equipments',
      url: SITE_BASE_URL,
    },
    about: {
      '@type': 'Thing',
      name: `${category.title} for heavy earth-moving equipment`,
    },
  };

  const applicationsListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${category.title} — typical applications`,
    itemListElement: category.applications.map((text, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: text,
    })),
  };

  const productsListSchema = hasItems
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `${category.title} — products`,
        numberOfItems: items.length,
        itemListElement: items.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `${SITE_BASE_URL}${itemPath(category.slug, item.slug)}`,
          name: `${item.name} ${item.partNumber}`,
        })),
      }
    : null;

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  return (
    <>
      <Helmet>
        <title>{category.seoTitle}</title>
        <meta name="description" content={category.seoDescription} />
        <meta name="keywords" content={category.keywords} />
        <link rel="canonical" href={currentUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={category.seoTitle} />
        <meta property="og:description" content={category.seoDescription} />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={category.seoTitle} />
        <meta name="twitter:description" content={category.seoDescription} />

        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(applicationsListSchema)}</script>
        {productsListSchema && (
          <script type="application/ld+json">{JSON.stringify(productsListSchema)}</script>
        )}
      </Helmet>

      <div className="min-h-screen bg-[#1a1a1a] flex flex-col font-sans">
        <Navigation />

        <main className="flex-grow pt-24 pb-16 px-4 sm:px-6 lg:px-8">
          <article className={`mx-auto ${hasItems ? 'max-w-7xl' : 'max-w-4xl'}`}>
            <Breadcrumb categoryTitle={category.title} />

            <motion.header
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-10 border-b border-[#2d2d2d] pb-8"
            >
              <p className="text-[#ff6b35] font-semibold text-sm tracking-wider uppercase mb-3">
                Heavy earth mover spare parts
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                {category.title}
              </h1>
              <p className="text-lg text-gray-300 leading-relaxed">{category.shortDescription}</p>
            </motion.header>

            <motion.section
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="max-w-none mb-12"
            >
              <p className="text-lg text-gray-300 leading-relaxed">{category.intro}</p>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="bg-[#2d2d2d]/60 rounded-xl p-6 sm:p-8 border border-[#3d3d3d] mb-12"
              aria-labelledby="applications-heading"
            >
              <h2 id="applications-heading" className="text-xl font-bold text-white mb-6">
                Typical applications
              </h2>
              <ul className="space-y-3">
                {category.applications.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle2 className="text-[#ff6b35] shrink-0 mt-0.5" size={20} aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.section>

            {hasItems && (
              <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18, duration: 0.5 }}
                className="mb-12"
                aria-labelledby="products-heading"
              >
                <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                  <div>
                    <h2 id="products-heading" className="text-2xl font-bold text-white">
                      Available {brandLabel} parts
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">
                      Showing {items.length} part{items.length === 1 ? '' : 's'} — click any SKU for full details and a quote.
                    </p>
                  </div>
                </div>

                <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map((item) => {
                    const placeholderSrc = buildProductPlaceholder({
                      partNumber: item.partNumber,
                      productName: item.name,
                      brandLabel,
                    });
                    const imgAlt = buildProductImageAlt({
                      productName: item.name,
                      partNumber: item.partNumber,
                      brandLabel,
                    });
                    return (
                      <li
                        key={`${category.slug}-${item.slug}`}
                        itemScope
                        itemType="https://schema.org/Product"
                      >
                        <Link
                          to={itemPath(category.slug, item.slug)}
                          className="block bg-[#2d2d2d] rounded-xl border border-[#3d3d3d] overflow-hidden hover:border-[#ff6b35]/60 hover:shadow-xl hover:shadow-[#ff6b35]/10 transition-all duration-300 group h-full flex flex-col"
                          aria-label={`View details for ${item.name} ${item.partNumber}`}
                        >
                          <div className="aspect-[4/3] bg-[#1f1f1f] overflow-hidden">
                            <img
                              src={placeholderSrc}
                              alt={imgAlt}
                              title={`${item.name} ${item.partNumber}`}
                              loading="lazy"
                              width="800"
                              height="600"
                              itemProp="image"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-5 flex-1 flex flex-col">
                            <h3
                              className="text-lg font-bold text-white mb-1 group-hover:text-[#ff6b35] transition-colors"
                              itemProp="name"
                            >
                              {item.name}
                            </h3>
                            <p className="text-xs uppercase tracking-wider text-gray-400 mb-3 font-mono">
                              <Tag size={12} className="inline mr-1 mb-0.5" aria-hidden="true" />
                              <span itemProp="sku">{item.partNumber}</span>
                            </p>
                            <p
                              className="text-sm text-gray-400 leading-relaxed flex-1"
                              itemProp="description"
                            >
                              {item.description.length > 140
                                ? `${item.description.slice(0, 140).trim()}…`
                                : item.description}
                            </p>
                            <span className="mt-4 text-sm font-semibold text-[#ff6b35] inline-flex items-center gap-1">
                              View details
                              <ChevronRight size={14} aria-hidden="true" />
                            </span>
                            <meta itemProp="brand" content="Trade Zone Equipments" />
                            <meta itemProp="mpn" content={item.partNumber} />
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </motion.section>
            )}

            {category.kind === 'brand' && !hasItems && (
              <motion.section
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18, duration: 0.5 }}
                className="bg-[#2d2d2d]/40 border border-dashed border-[#3d3d3d] rounded-xl p-8 text-center mb-12"
              >
                <p className="text-gray-300 font-semibold mb-2">Catalog coming online</p>
                <p className="text-gray-400 text-sm">
                  We&apos;re uploading SKU-level listings for this brand. Contact us in the meantime
                  for direct quote support — we ship from existing inventory.
                </p>
              </motion.section>
            )}

            <motion.nav
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between bg-[#2d2d2d] rounded-xl p-6 border border-[#4a4a4a]/30"
              aria-label="Related categories"
            >
              <div>
                <p className="text-white font-semibold mb-1">Need another system?</p>
                <p className="text-gray-400 text-sm">
                  Browse {PRODUCT_CATEGORIES.length} spare parts categories on the homepage.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  className="border-[#ff6b35] text-[#ff6b35] hover:bg-[#ff6b35] hover:text-white"
                  asChild
                >
                  <Link to="/#products">All categories</Link>
                </Button>
                <Button
                  className="bg-[#ff6b35] hover:bg-[#ff8c42] text-white"
                  type="button"
                  onClick={scrollToContact}
                >
                  Request a quote
                </Button>
              </div>
            </motion.nav>

            <div className="mt-10">
              <Link
                to="/#products"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-[#ff6b35] transition-colors text-sm font-medium"
              >
                <ArrowLeft size={16} aria-hidden="true" />
                Back to product categories
              </Link>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CategoryLanding;
